import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import type { MessageSequence } from '../types/messageSequence';

export interface SequencesState {
	items: MessageSequence[];
	loading: boolean;
	error: string | null;
	lastFetched: number | null;
}

const initialState: SequencesState = {
	items: [],
	loading: false,
	error: null,
	lastFetched: null,
};

export const fetchSequences = createAsyncThunk<
	MessageSequence[],
	void,
	{ rejectValue: string }
>('sequences/fetchAll', async (_, { rejectWithValue }) => {
	try {
		const { data } = await axios.get('/api/message-sequences');
		return Array.isArray(data) ? data : [];
	} catch (err) {
		const e = err as unknown as {
			response?: { data?: { message?: string } };
			message?: string;
		};
		return rejectWithValue(
			e?.response?.data?.message ||
				e?.message ||
				'Failed to load message sequences'
		);
	}
});

const sequencesSlice = createSlice({
	name: 'sequences',
	initialState,
	reducers: {
		setSequences(state, action: PayloadAction<MessageSequence[]>) {
			state.items = action.payload || [];
			state.lastFetched = Date.now();
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSequences.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchSequences.fulfilled, (state, action) => {
				state.loading = false;
				state.items = action.payload || [];
				state.lastFetched = Date.now();
			})
			.addCase(fetchSequences.rejected, (state, action) => {
				state.loading = false;
				state.error =
					(action.payload as string) || 'Failed to load sequences';
			});
	},
});

export const { setSequences } = sequencesSlice.actions;
export default sequencesSlice.reducer;
